<script setup lang="ts">
defineProps({
  upvoteCount: { type: Number, required: true },
  downvoteCount: { type: Number, required: true },
  comments: { type: Number, required: true },
  isUpvoted: { type: Boolean, required: true },
  isDownvoted: { type: Boolean, required: true },
  isPost: { type: Boolean, default: true },
  showReply: { type: Boolean, default: false }
})

defineEmits(['clickUpvote', 'clickDownvote', 'clickComment'])
</script>

<template>
  <div class="content-footer">
    <div class="content-footer-btns">
      <div
        :class="`post-btn ${isPost ? '' : 'post-btn-small'} clickable post-btn-upvote ${isUpvoted ? 'post-btn-upvote-fill' : ''}`"
        @click.stop="$emit('clickUpvote')"
      >
        <CommonIconForumVoteFill v-if="isUpvoted" />
        <CommonIconForumVote v-else />
        <span v-if="upvoteCount > 0">{{ upvoteCount }}</span>
        <div class="post-btn-tooltip anim-fade-up">
          <div class="post-btn-tooltip-text">Upvote</div>
        </div>
      </div>

      <div
        :class="`post-btn ${isPost ? '' : 'post-btn-small'} clickable post-btn-downvote ${isDownvoted ? 'post-btn-downvote-fill' : ''}`"
        @click.stop="$emit('clickDownvote')"
      >
        <CommonIconForumVoteFill v-if="isDownvoted" />
        <CommonIconForumVote v-else />
        <span v-if="downvoteCount > 0">{{ downvoteCount || 0 }}</span>
        <div class="post-btn-tooltip anim-fade-up">
          <div class="post-btn-tooltip-text">Downvote</div>
        </div>
      </div>

      <div
        v-if="isPost"
        :class="`post-btn ${isPost ? '' : 'post-btn-small'} clickable post-btn-comment`"
        @click.stop="$emit('clickComment')"
      >
        <CommonIconForumComment />
        <span>{{ comments }}</span>
        <div class="post-btn-tooltip anim-fade-up">
          <div class="post-btn-tooltip-text">{{ isPost ? 'Comments' : 'Replies' }}</div>
        </div>
      </div>

      <div
        v-else-if="showReply"
        :class="`post-btn ${isPost ? '' : 'post-btn-small'} clickable post-btn-comment`"
        @click.stop="$emit('clickComment')"
      >
        <CommonIconForumShare />
        <span>Reply</span>
      </div>
    </div>

    <div class="content-right">
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
.content-footer {
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &-btns {
    display: flex;
    align-items: center;
  }

  &-btn {
    font-size: 0.87rem;
    font-weight: 500;
    transition: opacity 0.3s ease-out;
    display: flex;
    align-items: center;
    gap: 10px;
    height: 35px;
    border: 1px solid var(--borderM2);
    border-radius: 7px;
    padding: 0 13px;

    svg,
    span {
      flex-shrink: 0;
    }

    svg {
      $svgSize: 13px;
      height: $svgSize;
      width: $svgSize;
    }
  }
}

.post-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 7px;
  border: 1px solid var(--borderM2);
  position: relative;
  transition:
    background 0.3s ease-out,
    border 0.3s ease-out;

  $svgSize: 14px;

  svg {
    height: $svgSize;
    width: $svgSize;
  }

  span {
    display: block;
    font-size: 0.95rem;
  }

  &-tooltip {
    height: 30px;
    position: absolute;
    top: -37px;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    display: none;

    &-text {
      background: rgb(61, 74, 87);
      font-size: 0.9rem;
      color: white;
      padding: 7px 10px;
      border-radius: 7px;
    }
  }

  &-upvote {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;

    &-fill {
      svg {
        fill: var(--forumGreen);
      }
    }
  }

  &-downvote {
    margin-right: 12px;
    border-left: 1px solid transparent;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;

    svg {
      transform: scaleY(-1);
    }

    &-fill {
      svg {
        fill: var(--forumRed);
      }
    }
  }

  &-small {
    padding: 0 10px;
    height: 27px;

    $svgSize: 12px;

    svg {
      height: $svgSize;
      width: $svgSize;
    }

    span {
      font-size: 0.9rem;
    }
  }
}

@media (hover: hover) {
  .post-btn:hover {
    .post-btn-tooltip {
      display: flex;
    }
  }

  .post-btn {
    &-upvote:hover {
      background-color: var(--forumLightGreen);
      border-color: var(--forumGreen);
    }

    &-downvote:hover {
      background-color: var(--forumLightRed);
      border-color: var(--forumRed);
    }

    &-comment:hover {
      background-color: var(--forumLightBlue);
      border-color: var(--forumBlue);
    }
  }
}
</style>
